import { useState, useEffect } from 'react';

import { trackLeadConversion } from '../utils/analytics';
import { COUNTRY_CODES, isPlausiblePhone, toInternationalDigits } from '../utils/phone';
import { captureAttribution } from '../utils/attribution';
import { useVoiceConsent, VoiceConsentCheckbox } from './forms/VoiceConsent';
const PROPCLOUD_API_BASE = import.meta.env.VITE_PROPCLOUD_API_URL || 'https://us-central1-propcloudai.cloudfunctions.net';

interface LeadForm {
  name: string;
  surname: string;
  mobile: string;
  email: string;
  enquiry: string;
}

type FormState = 'idle' | 'submitting' | 'success' | 'error';

/** Same check as functions/src/captureWhatsAppWebsiteLead.ts. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Country selector (Jaco, 2026-09-09): Namibian visitors typing local 081x
// numbers were silently normalized to nonexistent +27 numbers (Meta 131026 —
// undeliverable). DEFAULT STAYS SOUTH AFRICA; the form submits the full
// international number, which normalizePhoneE164 passes through unchanged.
// Same proven pattern as InvestmentSummaryForm (live since PR #747).

export default function WhatsAppLeadCapture() {
  const [visible, setVisible] = useState(false);
  const voice = useVoiceConsent();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<LeadForm>({ name: '', surname: '', mobile: '', email: '', enquiry: '' });
  const [countryCode, setCountryCode] = useState('27');
  const [state, setState] = useState<FormState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Check if the button should be shown (controlled via admin toggle)
  useEffect(() => {
    fetch(`${PROPCLOUD_API_BASE}/captureWhatsAppWebsiteLead`)
      .then(r => r.json())
      .then(data => setVisible(data.visible === true))
      .catch(() => setVisible(false));
  }, []);

  if (!visible) return null;

  const handleOpen = () => {
    setOpen(true);
    setState('idle');
    setErrorMsg('');
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ name: '', surname: '', mobile: '', email: '', enquiry: '' });
    setCountryCode('27');
    setState('idle');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.surname.trim() || !form.mobile.trim() || !form.email.trim()) return;
    // Audit 2026-09-19: this form never checked digit length, so a 1–2 digit
    // typo became a real lead with an undeliverable number. Same rule as the
    // other lead forms.
    if (!isPlausiblePhone(form.mobile)) {
      setErrorMsg('Please enter a valid WhatsApp number.');
      setState('error');
      return;
    }

    // Email is required (Jaco 2026-09-29): consultants send the calendar invite
    // to it. Same pattern the capture function validates with.
    if (!EMAIL_PATTERN.test(form.email.trim())) {
      setErrorMsg('Please enter a valid email address.');
      setState('error');
      return;
    }

    // Full international digits (shared normaliser — see utils/phone.ts)
    const internationalMobile = toInternationalDigits(form.mobile, countryCode);

    setState('submitting');
    setErrorMsg('');

    try {
      const response = await fetch(`${PROPCLOUD_API_BASE}/captureWhatsAppWebsiteLead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...voice.payload,
          name: form.name.trim(),
          surname: form.surname.trim(),
          mobile: internationalMobile,
          email: form.email.trim(),
          enquiry: form.enquiry.trim(),
          pageUrl: window.location.href,
          pageTitle: document.title,
          attribution: captureAttribution(),
        }),
      });

      if (!response.ok) throw new Error('Submission failed');
      setState('success');
      trackLeadConversion('whatsapp_lead_capture');
    } catch {
      setState('error');
      setErrorMsg('Something went wrong. Please try again or WhatsApp us directly.');
    }
  };

  return (
    <>
      {/* Floating WhatsApp Button */}
      <button
        onClick={handleOpen}
        aria-label="Chat with us on WhatsApp"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-[#25D366] hover:bg-[#128C7E] text-white px-7 py-4 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all font-bold text-base md:text-lg"
      >
        {/* WhatsApp SVG icon */}
        <svg viewBox="0 0 24 24" className="w-7 h-7 md:w-8 md:h-8 fill-current" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span>WhatsApp Us</span>
      </button>

      {/* Modal Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto overflow-hidden">
            {/* Header */}
            <div className="bg-[#25D366] px-6 pt-6 pb-8">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-white/70 hover:text-white transition"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg leading-tight">Chat with a PropInvest Expert</h2>
                  <p className="text-white/80 text-sm">We'll WhatsApp you within minutes</p>
                </div>
              </div>
            </div>

            {/* Form / Success */}
            <div className="px-6 py-6 -mt-4 bg-white rounded-t-2xl relative z-10">
              {state === 'success' ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-[#25D366]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">We have your details!</h3>
                  <p className="text-gray-500 text-sm mb-6">
                    We're connecting you with a consultant now — please check your WhatsApp.
                  </p>
                  <button
                    onClick={handleClose}
                    className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold py-3 rounded-xl transition"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Leave your details and a <strong>PropInvest expert</strong> will reach out to you on WhatsApp to discuss your investment goals — no pressure, no obligation.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. John"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      required
                      value={form.surname}
                      onChange={(e) => setForm({ ...form, surname: e.target.value })}
                      placeholder="e.g. Smith"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                    <div className="flex gap-2">
                      <select
                        aria-label="Country code"
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="border border-gray-200 rounded-xl px-2 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        required
                        value={form.mobile}
                        onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                        placeholder="e.g. 082 123 4567"
                        className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="e.g. john@example.com"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      So we can send your consultation calendar invite
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      What can we help you with? <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      value={form.enquiry}
                      onChange={(e) => setForm({ ...form, enquiry: e.target.value })}
                      placeholder="Tell us what you're interested in — a specific development, budget, or a question"
                      rows={3}
                      maxLength={1000}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                    />
                  </div>

                  {state === 'error' && (
                    <p className="text-red-500 text-sm bg-red-50 px-4 py-3 rounded-xl">{errorMsg}</p>
                  )}

                  <VoiceConsentCheckbox consent={voice.consent} ticked={voice.ticked} setTicked={voice.setTicked} className="mb-3" />

                  <button
                    type="submit"
                    disabled={state === 'submitting'}
                    className="w-full bg-[#25D366] hover:bg-[#128C7E] disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl transition flex items-center justify-center gap-2"
                  >
                    {state === 'submitting' ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        Send My Details
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-400 text-center">
                    By submitting, you agree to be contacted by a PropInvest consultant via WhatsApp.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
